import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BandeiraCartao, BuscarPecasRow, CanalOrcamento, PaymentMethod, PecaParaOCarro, StoreCredit,
} from '@core/types';
import Layout from '../componentes/Layout';
import BotaoJogo from '../componentes/BotaoJogo';
import BuscaPecas from '../componentes/BuscaPecas';
import BuscaGuiada from '../componentes/BuscaGuiada';
import BuscaPlaca from '../componentes/BuscaPlaca';
import NavegarCatalogo from '../componentes/NavegarCatalogo';
import OndeFica, { type AcaoNoOndeFica, type PecaNaFolha } from '../componentes/OndeFica';
import SeletorCliente from '../componentes/SeletorCliente';
import SeletorVeiculo, { CARRO_VAZIO, carroPreenchido, textoDoCarro } from '../componentes/SeletorVeiculo';
import type { CarroEscolhido } from '../componentes/SeletorVeiculo';
import { carroDoRascunho, carroParaRpc } from '../lib/carro';
import Aviso from '../componentes/Aviso';
import MiniaturaDaPeca from '../componentes/MiniaturaDaPeca';
import EscolherParcelas from '../componentes/EscolherParcelas';
import { NOME_DA_BANDEIRA, taxaDaFaixa } from '../lib/parcelamento';
import {
  acharAVer, atualizarPrecoBalcao, emitirOrcamento, fotosDasPecas, limitesDaOperacao, limitesEfetivos,
  lugaresDasPecas, meuCarrinho, obterPeca, pecaCoringa, tiposDasPecas,
  veiculosDoCliente,
  registrarVenda, renovarCarrinho, segurarCarrinho, soltarCarrinho, taxasDeCartao,
} from '../lib/consultas';
import { numeroParaTexto, percentualParaValor, valorParaPercentual } from '../lib/desconto';
import { brl, hojeISO, paraNumero, qtd } from '../lib/formato';
import {
  conferirPartes, FORMAS_QUE_DIVIDEM, partesParaRpc, restanteDaParte, type ParteDigitada,
} from '../lib/pagamentos';
import { rotuloDaForma } from '../lib/rotulos';
import { precoVigente } from '../lib/promocao';
import { contemCoringa, nomeDoItemVendido, observacaoCobreCoringa, SKU_CORINGA } from '../lib/coringa';
import { unidadesDoJogo } from '../lib/jogo';
import CampoNumero from '../componentes/CampoNumero';
import { BarraCarrinho, CarrinhoLateral } from '../componentes/CarrinhoLateral';
import { CANAIS_ORCAMENTO, validadePadrao } from '../lib/orcamento';
import {
  chaveDoRascunho, lerRascunho, limparRascunho, mesclarDisponivel, salvarRascunho,
} from '../lib/rascunhoVenda';
import { lugaresParaMostrar, type LugarDaPeca } from '../lib/ondeFica';
import { mensagemDeErro } from '../lib/supabase';
import { podeGerenciarCatalogo, useSessao } from '../lib/sessao';

// Tela 5 — venda em 30 segundos. Regras que sustentam o cronômetro:
//   * a busca já está aberta e focada; achou, clicou, entrou no carrinho com 1;
//   * preço vem preenchido do preco_balcao — digitar é exceção, não regra;
//   * a forma de pagamento são botões grandes, não um <select>;
//   * o commit é UMA chamada: a RPC registrar_venda faz venda + baixa de
//     estoque + movimento + caixa numa transação. Se qualquer parte falhar,
//     nada acontece — não existe venda pela metade.
//   * o MESMO carrinho vira orçamento (tela 15): em vez de fechar, emite o
//     documento com os preços congelados — sem tocar estoque nem caixa.

export const FORMAS: Array<{ id: PaymentMethod; rotulo: string }> = [
  { id: 'dinheiro', rotulo: 'Dinheiro' },
  { id: 'pix', rotulo: 'PIX' },
  { id: 'debito', rotulo: 'Débito' },
  { id: 'credito', rotulo: 'Crédito' },
  { id: 'transferencia', rotulo: 'Transf.' },
  { id: 'boleto', rotulo: 'Boleto' },
  { id: 'fiado', rotulo: 'Fiado' },
  // 075: "cem em dinheiro e o resto no cartão". Não é uma forma de pagar — é
  // o botão que abre as PARTES; a venda fica gravada como 'misto' e o caixa
  // recebe uma entrada por parte, cada uma pela sua forma.
  { id: 'misto', rotulo: 'Dividir' },
];

/** As partes com que a divisão abre: dinheiro + crédito é o caso do balcão.
 *  O vendedor troca a forma, digita uma e usa "resto" na outra. */
const PARTES_INICIAIS: ParteDigitada[] = [
  { forma: 'dinheiro', valorTexto: '' },
  { forma: 'credito', valorTexto: '' },
];

/** O botão de ESCOLHA (forma de pagar, canal do orçamento) no Grafite: o
 *  escolhido ganha a borda clara e o degrau de cinza, nunca uma cor; 44 px de
 *  alvo, que é o dedo de quem está em pé no balcão. */
const botaoDeEscolha = (ativo: boolean) => [
  'min-h-11 rounded-[0.625rem] border px-2 py-2 text-sm font-medium transition-colors',
  ativo ? 'border-paper bg-panel-alt text-paper' : 'border-edge-forte text-mute hover:text-paper',
].join(' ');

/** O botão TERCIÁRIO do Grafite: só texto, sem borda nem fundo em repouso. */
const BOTAO_TERCIARIO = 'inline-flex min-h-11 w-full items-center justify-center rounded-[0.625rem] px-3 '
  + 'text-sm font-medium text-mute transition-colors hover:bg-panel-alt hover:text-paper '
  + 'disabled:cursor-not-allowed disabled:opacity-45';

/**
 * O TOTAL da venda (Grafite): claro, grande e em mono, com os algarismos
 * alinhados. Até 28/10/2026 ele era o vermelho da MARCA — e vermelho em número
 * de dinheiro se lê como prejuízo (regra 10 do CLAUDE.md). Fica num componente
 * à parte para o teste poder desenhá-lo: no primeiro quadro da Venda o
 * carrinho está vazio e o fechamento nem aparece.
 */
export function TotalDaVenda({ rotulo, valor }: { rotulo: string; valor: number }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-[0.9375rem] text-paper">{rotulo}</span>
      <span className="numero text-3xl font-medium text-paper sm:text-4xl" data-total-da-venda="">
        {brl(valor)}
      </span>
    </div>
  );
}

interface ItemCarrinho {
  product_id: string;
  sku: string;
  descricao: string;
  quantidade: number;
  preco_unit: number;
  disponivel: number;
  /**
   * Identidade da LINHA, não da peça (041). Só a PEÇA DIVERSA usa: cinco peças
   * diversas diferentes são cinco linhas do mesmo product_id, cada uma com seu
   * preço — antes viravam "5 × PEÇA DIVERSA" a um preço só, que foi exatamente
   * o que o dono reclamou. Peça cadastrada continua agrupando por product_id,
   * que é o certo: 3 do mesmo filtro é quantidade 3.
   */
  linha?: string;
  /** O que REALMENTE saiu nesta linha — só no coringa. Vai para o banco. */
  descricao_avulsa?: string;
  /** PEÇA DIVERSA (071): o CÓDIGO daquela peça. Existe para que ele PARE de
   *  ser escrito dentro do nome — o nome é o que sai no papel do cliente, e o
   *  código é nosso. Vai para o banco e para a tela da loja, nunca para o
   *  documento. */
  codigo_avulso?: string;
  /** PEÇA DIVERSA (048): o custo digitado. Sugere o preço (custo × regra da
   *  casa) e vira o custo_unit do CMV — hoje a diversa vendia com custo zero. */
  custo_avulso?: number | null;
  /** 073 — o carro DESTA linha, quando ela é de um carro diferente do carro da
   *  venda. Vazio quer dizer "o carro da venda": nada de repetir o mesmo carro
   *  em toda linha. Diferente de `codigo_avulso`, vale em peça cadastrada. */
  veiculo_montadora?: string;
  veiculo_modelo?: string;
  /** O que o vendedor DIGITOU no carro desta linha (08/10/2026) — só tela e
   *  rascunho; ao banco vão montadora e modelo já lidos (lib/carro.ts). */
  veiculo_texto?: string;
  /** O preço VIGENTE no catálogo quando a peça entrou (19/08/2026). É a
   *  referência que revela a divergência — e o que o botão "atualizar preço"
   *  da gerência conserta. Nulo na peça diversa (não tem tabela). */
  preco_tabela?: number | null;
}

/** Carrinho pré-montado via location.state — usado pelo "emitir igual" de /orcamentos/:id. */
export interface CarrinhoPrefill {
  itens: Array<{
    product_id: string; quantidade: number; preco_unit: number;
    /** A descrição CONGELADA do documento (041): é o nome da peça diversa que
     *  o cliente leu no papel. Sem ela, "emitir igual" devolvia o genérico. */
    descricao?: string;
    /** O código congelado (071). Volta junto para o vendedor não ter de
     *  procurá-lo de novo no catálogo do fornecedor. */
    codigo?: string;
  }>;
  desconto?: number;
  cliente?: string | null;
  contato?: string | null;
  veiculo?: string | null;
}

// Os mesmos três modos da tela de Busca (pedido do dono, 07/08/2026): o
// vendedor que não tem o código na mão fecha a venda pela cascata
// montadora → modelo → ano, sem sair da tela.
// Desde 01/10/2026 também Por tipo e Por carro (ADR-009): "Fiat → Palio → óleo"
// põe no carrinho o óleo que o Palio pede.
type ModoBusca = 'livre' | 'tipo' | 'carro' | 'veiculo' | 'placa';

const MODOS_BUSCA: Array<{ id: ModoBusca; rotulo: string }> = [
  { id: 'livre', rotulo: 'Livre' },
  { id: 'tipo', rotulo: 'Por tipo' },
  { id: 'carro', rotulo: 'Por carro' },
  { id: 'veiculo', rotulo: 'Por versão' },
  { id: 'placa', rotulo: 'Por placa' },
];

/**
 * MODO (17/08/2026, pedido do dono): a MESMA máquina de carrinho serve duas
 * abas. Em `/venda` (modo 'venda') tudo como sempre. Em `/orcar` (modo
 * 'orcamento') a tela é SÓ orçamento: some o fechamento de venda (forma de
 * pagamento, a-ver, fiado, confirmar) e a emissão fica à vista direto — o
 * vendedor monta o carrinho e emite, sem passar perto do botão de vender.
 * O carrinho no banco é o MESMO (a reserva é do atendimento, não da aba).
 */
export default function Venda({ modo = 'venda' }: { modo?: 'venda' | 'orcamento' }) {
  // No topo porque o rascunho (19/08/2026) precisa do perfil para montar a
  // chave — o balcão é computador compartilhado, e cada um tem o seu.
  const { papel, perfil } = useSessao();
  const navigate = useNavigate();
  const local = useLocation();
  const cliente = useQueryClient();

  const [modoBusca, setModoBusca] = useState<ModoBusca>('livre');
  const [itens, setItens] = useState<ItemCarrinho[]>([]);
  // As fotos das peças do carrinho, num lote só (01/10/2026).
  const idsDoCarrinho = [...new Set(itens.filter((i) => !i.linha && i.sku !== SKU_CORINGA).map((i) => i.product_id))];
  const fotosDoCarrinho = useQuery({
    queryKey: ['fotos-do-carrinho', idsDoCarrinho],
    queryFn: () => fotosDasPecas(idsDoCarrinho),
    enabled: idsDoCarrinho.length > 0,
    staleTime: 60_000,
    // Cada peça bipada troca a chave: sem isto, todas as miniaturas sumiam e
    // as linhas pulavam até a resposta chegar (revisão do #254).
    placeholderData: keepPreviousData,
  });
  // ONDE BUSCAR cada peça do carrinho (06/10/2026). O lugar NÃO entra no
  // item nem no rascunho: vem do banco, na mesma chave da busca. Se o dono
  // mudar a peça de lugar no meio do atendimento, o carrinho já mostra o novo.
  const lugaresDoCarrinho = useQuery({
    queryKey: ['lugares-das-pecas', idsDoCarrinho],
    queryFn: () => lugaresDasPecas(idsDoCarrinho),
    enabled: idsDoCarrinho.length > 0,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
  // O TIPO de cada peça do carrinho, num lote só (08/10/2026) — é dele que
  // nasce o botão "Jogo". Fora do item e fora do rascunho, pelo mesmo motivo
  // do lugar: o rascunho gravado antes desta mudança não teria o campo, e o
  // botão sumiria justamente nos atendimentos em curso.
  const tiposDoCarrinho = useQuery({
    queryKey: ['tipos-das-pecas', idsDoCarrinho],
    queryFn: () => tiposDasPecas(idsDoCarrinho),
    enabled: idsDoCarrinho.length > 0,
    // Tipo de peça praticamente não muda; e sem o placeholder o botão piscava
    // a cada bipe, porque a chave da consulta troca junto com o carrinho.
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
  });
  // A folha "Onde fica" aberta a partir do carrinho: uma linha, ou a lista toda.
  const [ondeFica, setOndeFica] = useState<{ tipo: 'peca'; chave: string } | { tipo: 'lista' } | null>(null);
  const [forma, setForma] = useState<PaymentMethod>('dinheiro');
  // 087: a BANDEIRA e EM QUANTAS VEZES, no credito. Nulos fora dele - e
  // zerados quando o vendedor troca a forma, senao a bandeira escolhida numa
  // tentativa de credito viajaria numa venda em dinheiro.
  const [cartao, setCartao] = useState<{ bandeira: BandeiraCartao; parcelas: number } | null>(null);
  const [escolhendoParcelas, setEscolhendoParcelas] = useState(false);
  // 075: as PARTES do pagamento dividido — só valem quando forma === 'misto'.
  // Não vão para o rascunho: são o fechamento, não o atendimento.
  const [partes, setPartes] = useState<ParteDigitada[]>(PARTES_INICIAIS);
  const [descontoTexto, setDescontoTexto] = useState('');
  const [entregaTexto, setEntregaTexto] = useState('');
  // O espelho em % (12/08/2026): digitar um preenche o outro. Quem vai ao
  // banco é SEMPRE o R$; o percentual é conveniência de digitação. `origem`
  // lembra qual dos dois o vendedor digitou por último — é ele que manda
  // quando o carrinho muda (item entra, o bruto sobe: 10% recalcula o R$;
  // R$ 20 fixos recalculam o %).
  const [descontoPctTexto, setDescontoPctTexto] = useState('');
  const origemDesconto = useRef<'valor' | 'pct' | null>(null);
  const [nomeCliente, setNomeCliente] = useState('');
  // Cliente CADASTRADO (migração 023). O nome digitado continua valendo sozinho
  // — nem toda venda de balcão precisa de cadastro —, mas quando há id a venda
  // entra no histórico da pessoa.
  const [clienteId, setClienteId] = useState<string | null>(null);
  const [observacao, setObservacao] = useState('');
  // 073 — o carro da venda. Desde 08/10/2026 DIGITADO num campo só ("gol 2010
  // 1.0") e lido por lib/carro.ts: a montadora sai na grafia da FIPE e o
  // modelo com o nome-base na frente. Opcional: venda de fusível e lâmpada
  // muitas vezes não tem carro, e exigir um faria o balcão inventar um, que é
  // pior que o vazio.
  const [carro, setCarro] = useState<CarroEscolhido>(CARRO_VAZIO);
  const [concluida, setConcluida] = useState<string | null>(null);
  // O que saiu na última venda. Guardado ANTES de o carrinho ser limpo: sem
  // isto a tela dizia só "venda registrada", e a peça diversa que o vendedor
  // acabou de descrever sumia junto com o carrinho (pedido do dono, 17/08).
  const [vendido, setVendido] = useState<ItemCarrinho[]>([]);
  const [codigoAVer, setCodigoAVer] = useState('');
  // Uma chave por TENTATIVA de venda. Enquanto o carrinho for o mesmo, tocar
  // duas vezes em "confirmar" (ou a rede reenviar) devolve a venda já criada em
  // vez de criar outra, com baixa de estoque e lançamento de caixa em dobro.
  // Renovada só depois que a venda fecha — ver registrarVenda em consultas.ts.
  const [chaveVenda, setChaveVenda] = useState(() => crypto.randomUUID());
  const [aVer, setAVer] = useState<StoreCredit | null>(null);
  // Encerrar o vale abonando a sobra (migração 034) — só faz sentido com um
  // vale aplicado que cobre mais que a compra; zera junto com o vale.
  const [abonarSobra, setAbonarSobra] = useState(false);
  // O carrinho é FATO no banco desde a migração 026: cada item aqui é uma
  // reserva de 30 min, visível aos colegas ("Luiz reservou 2 un"). O relógio da
  // reserva mora em expiraCarrinho; a recusa por disputa ("no carrinho de
  // Fulano") mora em erroCarrinho, perto de onde o clique aconteceu.
  const [expiraCarrinho, setExpiraCarrinho] = useState<string | null>(null);
  const [erroCarrinho, setErroCarrinho] = useState<string | null>(null);
  const [agora, setAgora] = useState(() => Date.now());
  // A quantidade CONHECIDA de cada item, fora do estado: dois bips seguidos do
  // mesmo código chegam antes de o setItens do primeiro commitar, e calcular a
  // nova quantidade a partir do estado perderia um deles.
  const qtdRef = useRef<Record<string, number>>({});
  const [orcando, setOrcando] = useState(modo === 'orcamento');
  const [contatoCliente, setContatoCliente] = useState('');
  const [validade, setValidade] = useState(() => validadePadrao(hojeISO()));
  // 049: de ONDE veio a cotação, e o carro como o cliente falou. Balcão é o
  // padrão porque é o caso de quem está com o cliente na frente.
  const [canalOrc, setCanalOrc] = useState<CanalOrcamento>('balcao');
  const [veiculoOrc, setVeiculoOrc] = useState('');
  // 051 — qual carro DO CLIENTE esta cotação atende. Só existe com cliente
  // cadastrado escolhido; sem ele, o texto livre acima segue valendo sozinho.
  const [carroOrc, setCarroOrc] = useState('');
  const [obsOrc, setObsOrc] = useState('');

  // Veio da ficha da peça ("Vender"): já entra com o item no carrinho.
  const prefill = (local.state as { productId?: string } | null)?.productId;
  useEffect(() => {
    if (!prefill) return;
    let vivo = true;
    obterPeca(prefill).then(({ produto, estoque }) => {
      if (!vivo) return;
      adicionar({
        product_id: produto.id,
        sku: produto.sku,
        descricao: produto.descricao,
        // precoVigente: peça em promoção entra pelo preço promocional.
        preco_unit: precoVigente(produto) ?? 0,
        preco_tabela: precoVigente(produto) ?? null,
        disponivel: estoque?.quantidade ?? 0,
        quantidade: 1,
      });
      navigate('/venda', { replace: true, state: null });
    }).catch(() => undefined);
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  // Veio de /orcamentos/:id ("emitir igual"): carrinho inteiro pré-montado com
  // os preços do orçamento antigo — o vendedor revisa e emite/atualiza.
  const carrinho = (local.state as { carrinho?: CarrinhoPrefill } | null)?.carrinho;
  useEffect(() => {
    if (!carrinho) return;
    let vivo = true;
    Promise.all(carrinho.itens.map(async (i) => {
      const { produto, estoque } = await obterPeca(i.product_id);
      const ehCoringa = produto.sku === SKU_CORINGA;
      return {
        product_id: produto.id,
        sku: produto.sku,
        descricao: produto.descricao,
        preco_unit: i.preco_unit,
        preco_tabela: ehCoringa ? null : precoVigente(produto) ?? null,
        disponivel: estoque?.quantidade ?? 0,
        quantidade: i.quantidade,
        // Peça diversa volta como LINHA própria, com o nome que estava no
        // documento — é o que o cliente tem na mão.
        ...(ehCoringa
          ? {
            linha: crypto.randomUUID(),
            descricao_avulsa: i.descricao ?? '',
            codigo_avulso: i.codigo ?? '',
          }
          : {}),
      } satisfies ItemCarrinho;
    })).then((novos) => {
      if (!vivo) return;
      setItens(novos);
      setDescontoTexto(carrinho.desconto ? String(carrinho.desconto).replace('.', ',') : '');
      origemDesconto.current = carrinho.desconto ? 'valor' : null;
      setNomeCliente(carrinho.cliente ?? '');
      setContatoCliente(carrinho.contato ?? '');
      setVeiculoOrc(carrinho.veiculo ?? '');
      setOrcando(true);
      navigate('/venda', { replace: true, state: null });
    }).catch(() => undefined);
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carrinho]);

  // O RASCUNHO (19/08/2026): trocar de aba não apaga o atendimento.
  //
  // O carrinho do banco (026) devolve as PEÇAS reservadas; o que ele não tem é
  // o que o vendedor DIGITOU — peça diversa, preço combinado, desconto, nome
  // do cliente, observação. Isso fica no aparelho e volta aqui. Ver
  // lib/rascunhoVenda.ts para o porquê de cada decisão.
  const chaveRascunho = chaveDoRascunho(perfil?.id);
  const restaurouRef = useRef(false);

  useEffect(() => {
    if (prefill || carrinho || restaurouRef.current) return;
    restaurouRef.current = true;
    const r = lerRascunho(chaveRascunho);
    if (!r) return;
    setItens((atuais) => {
      if (atuais.length > 0) return atuais;
      // O `disponivel` não vem do rascunho de propósito: estoque é do banco, e
      // a busca/ficha o atualiza. Zero aqui só faz a linha avisar "acima do
      // estoque" até a próxima leitura — melhor que mostrar saldo velho.
      r.itens.forEach((i) => { if (!i.linha) qtdRef.current[i.product_id] = i.quantidade; });
      return r.itens.map((i) => ({ ...i, disponivel: 0 }));
    });
    setDescontoTexto((a) => a || r.descontoTexto);
    setDescontoPctTexto((a) => a || r.descontoPctTexto);
    setNomeCliente((a) => a || r.nomeCliente);
    setClienteId((a) => a ?? r.clienteId);
    setObservacao((a) => a || r.observacao);
    // O rascunho anterior a 08/10/2026 não tem o texto: volta pelas partes, e
    // o campo remonta a frase (lib/carro.ts, carroDoRascunho).
    const carroGuardado = carroDoRascunho(r);
    if (carroGuardado) {
      setCarro((a) => (carroPreenchido(a) || a.texto?.trim() ? a : carroGuardado));
    }
    setContatoCliente((a) => a || r.contatoCliente || '');
    setVeiculoOrc((a) => a || r.veiculoOrc || '');
    setObsOrc((a) => a || r.obsOrc || '');
    if (r.canalOrc) setCanalOrc(r.canalOrc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chaveRascunho, prefill, carrinho]);

  // Guardar a cada mudança do que é digitado. Sem debounce: escrever no
  // localStorage é síncrono e barato, e o risco que interessa (fechar a aba no
  // meio) não espera 300 ms.
  useEffect(() => {
    if (!restaurouRef.current) return;
    salvarRascunho(chaveRascunho, {
      itens: itens.map((i) => ({
        product_id: i.product_id, sku: i.sku, descricao: i.descricao,
        quantidade: i.quantidade, preco_unit: i.preco_unit,
        linha: i.linha, descricao_avulsa: i.descricao_avulsa,
        custo_avulso: i.custo_avulso ?? null,
        preco_tabela: i.preco_tabela ?? null,
        veiculo_montadora: i.veiculo_montadora,
        veiculo_modelo: i.veiculo_modelo,
        veiculo_texto: i.veiculo_texto,
      })),
      descontoTexto, descontoPctTexto, nomeCliente, clienteId, observacao,
      // O ORÇAMENTO também não pode sumir ao trocar de aba (19/08/2026): a
      // ficha da cotação é digitada tanto quanto o carrinho.
      contatoCliente, veiculoOrc, obsOrc, canalOrc,
      carroMontadora: carro.montadora,
      carroModelo: carro.modelo,
      carroAno: carro.ano,
      carroVeiculoId: carro.veiculoId,
      carroTexto: carro.texto,
    });
  }, [chaveRascunho, itens, descontoTexto, descontoPctTexto, nomeCliente,
      clienteId, observacao, contatoCliente, veiculoOrc, obsOrc, canalOrc, carro]);

  // O carrinho renasce do banco: F5, aba fechada, celular trocado no meio do
  // atendimento — o que estava reservado volta, com os preços VIGENTES de agora
  // (reserva não congela preço; documento emitido sim). Só quando a tela abre
  // vazia e sem prefill, e só se o vendedor ainda não digitou nada — é a mesma
  // trava de "tocado" do CLAUDE.md.
  useEffect(() => {
    if (prefill || carrinho) return;
    let vivo = true;
    meuCarrinho().then((linhas) => {
      if (!vivo || linhas.length === 0) return;
      setItens((atuais) => {
        // A lista já veio cheia do rascunho (volta do mapa grande, da ficha):
        // da reserva só se aproveita o ESTOQUE, peça a peça — nunca quantidade,
        // preço ou linha digitada (lib/rascunhoVenda.ts, mesclarDisponivel).
        if (atuais.length > 0) return mesclarDisponivel(atuais, linhas);
        linhas.forEach((l) => { qtdRef.current[l.product_id] = l.quantidade; });
        return linhas.map((l) => ({
          product_id: l.product_id,
          sku: l.sku,
          descricao: l.descricao,
          quantidade: l.quantidade,
          preco_unit: precoVigente(l) ?? 0,
          preco_tabela: precoVigente(l) ?? null,
          disponivel: l.disponivel,
        }));
      });
      setExpiraCarrinho(linhas.reduce((m, l) => (l.expira_em < m ? l.expira_em : m), linhas[0]!.expira_em));
      const nome = linhas.find((l) => l.cliente_nome)?.cliente_nome;
      if (nome) setNomeCliente((atual) => atual || nome);
    }).catch(() => undefined);
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const adicionar = (item: ItemCarrinho) => {
    setConcluida(null);
    setErroCarrinho(null);

    // PEÇA DIVERSA (041): cada uma é uma LINHA nova, com preço e descrição
    // próprios. Não passa pela reserva de carrinho de propósito — o coringa é
    // curinga sem estoque documentado, e reservar o que não existe não protege
    // ninguém (a regra "só o documentado é disputável", da v5/v9).
    if (item.sku === SKU_CORINGA) {
      // A ÚLTIMA ADICIONADA FICA EM CIMA (pedido do dono, 08/09/2026). Com o
      // item novo no fim, uma venda de seis peças empurrava para fora da tela
      // justamente a que o vendedor acabou de bipar — e ele rolava a lista
      // para conferir se entrou. No topo, a confirmação é imediata.
      //
      // A CHAVE PODE VIR DE FORA (08/10/2026): quem chama precisa saber qual
      // linha nasceu para pôr o cursor nela. Sem isso, o botão da peça diversa
      // teria de repetir este caminho inteiro — e dois caminhos para a mesma
      // linha divergem na primeira mudança.
      setItens((atuais) => [{
        ...item,
        linha: item.linha ?? crypto.randomUUID(),
        descricao_avulsa: '',
        codigo_avulso: '',
      }, ...atuais]);
      return;
    }

    const nova = (qtdRef.current[item.product_id] ?? 0) + item.quantidade;
    qtdRef.current[item.product_id] = nova;
    // A reserva vem ANTES de o item entrar na tela: se a peça está disputada, o
    // vendedor descobre AGORA — não no fim do atendimento, com o cliente na
    // frente. É a razão de este carrinho existir.
    segurarCarrinho(item.product_id, nova, nomeCliente.trim() || null)
      .then((expira) => {
        setExpiraCarrinho(expira);
        setItens((atuais) => {
          const i = atuais.findIndex((x) => x.product_id === item.product_id);
          // Peça NOVA entra em cima (08/09/2026). A que JÁ estava no carrinho
          // só soma quantidade e fica onde está: subir uma linha que o
          // vendedor já conferiu embaralharia a lista debaixo do dedo dele.
          if (i === -1) return [{ ...item, quantidade: nova }, ...atuais];
          const copia = [...atuais];
          copia[i] = { ...copia[i]!, quantidade: nova };
          return copia;
        });
      })
      .catch((e) => {
        qtdRef.current[item.product_id] = nova - item.quantidade;
        if (qtdRef.current[item.product_id] === 0) delete qtdRef.current[item.product_id];
        setErroCarrinho(mensagemDeErro(e));
      });
  };

  /** Quantidade muda otimista (o balcão não espera), e a disputa desfaz. */
  const mudarQuantidade = (id: string, nova: number, ehLinhaAvulsa = false) => {
    if (nova <= 0) return;
    // Peça diversa não reserva estoque: muda a linha e pronto.
    if (ehLinhaAvulsa) { alterar(id, { quantidade: nova }); return; }
    const anterior = qtdRef.current[id] ?? 1;
    qtdRef.current[id] = nova;
    alterar(id, { quantidade: nova });
    setErroCarrinho(null);
    segurarCarrinho(id, nova, nomeCliente.trim() || null)
      .then(setExpiraCarrinho)
      .catch((e) => {
        qtdRef.current[id] = anterior;
        alterar(id, { quantidade: anterior });
        setErroCarrinho(mensagemDeErro(e));
      });
  };

  // A PEÇA DIVERSA, num toque (08/10/2026).
  //
  // O dono: "adicione um botão para que fique mais fácil para os colaboradores
  // colocarem uma peça diversa enquanto não temos tudo cadastrado". Hoje o
  // caminho é digitar na busca e clicar no resultado — e 96% do que a loja vende
  // sai por aqui, então é o gesto mais repetido do balcão.
  //
  // A consulta é uma linha só e fica em cache o atendimento inteiro: o botão
  // precisa estar pronto no primeiro toque, não depois de um ida-e-volta com o
  // cliente esperando.
  const coringa = useQuery({
    queryKey: ['peca-coringa'], queryFn: pecaCoringa, staleTime: 60 * 60_000,
  });

  // Passa pelo MESMO `adicionar` da busca: é a mesma linha, com a mesma régua
  // (descrição obrigatória, sem reserva de estoque). O que o botão acrescenta é
  // o foco — o campo "Que peça é esta?" é o que falta para a venda poder
  // fechar, e deixar o cursor nele é a diferença entre um toque e três.
  const adicionarPecaDiversa = () => {
    const p = coringa.data;
    if (!p) return;
    const linha = crypto.randomUUID();
    adicionar({
      product_id: p.id,
      sku: p.sku,
      descricao: p.descricao,
      preco_unit: precoVigente(p) ?? 0,
      preco_tabela: precoVigente(p) ?? null,
      disponivel: 0,
      quantidade: 1,
      linha,
    });
    // Depois do quadro em que a linha é desenhada. `preventScroll` porque a
    // linha nasce no TOPO da lista: rolar até ela seria rolar para onde o
    // vendedor já está olhando.
    requestAnimationFrame(() => {
      document.getElementById(`avulsa-${linha}`)?.focus({ preventScroll: true });
    });
  };

  // A peça escolhida pelo catálogo (Por tipo / Por carro) entra igual à da busca.
  const doCatalogo = (p: PecaParaOCarro) => adicionar({
    product_id: p.product_id,
    sku: p.sku,
    descricao: p.descricao,
    preco_unit: precoVigente(p) ?? 0,
    preco_tabela: precoVigente(p) ?? null,
    disponivel: p.quantidade,
    quantidade: 1,
  });

  const doResultado = (p: BuscarPecasRow) => adicionar({
    product_id: p.product_id,
    sku: p.sku,
    descricao: p.descricao,
    // O carrinho nasce com o preço que o cartão mostrou: promocional quando a
    // peça está em promoção, balcão no resto. Editar continua livre.
    preco_unit: precoVigente(p) ?? 0,
    preco_tabela: precoVigente(p) ?? null,
    disponivel: p.quantidade,
    quantidade: 1,
  });

  /**
   * A chave da LINHA do carrinho (041). Peça cadastrada é identificada pelo
   * produto — 3 do mesmo filtro é quantidade 3, e mexer numa mexe na linha
   * certa. A PEÇA DIVERSA tem `linha` própria, porque cinco peças diversas
   * são cinco linhas do MESMO product_id e precisam de preço, descrição e
   * botão de remover independentes.
   */
  const chaveDe = (i: ItemCarrinho) => i.linha ?? i.product_id;

  // --- Onde fica (06/10/2026) ------------------------------------------------
  // Peça diversa e coringa não têm lugar: são texto livre, sem prateleira.
  const semPrateleira = (i: ItemCarrinho) => Boolean(i.linha) || i.sku === SKU_CORINGA;
  const lugaresDoItem = (i: ItemCarrinho): LugarDaPeca[] => {
    if (semPrateleira(i)) return [];
    const l = lugaresDoCarrinho.data?.get(i.product_id);
    return l ? lugaresParaMostrar(l.principal, l.extras) : [];
  };
  // Calota (15/10/2026): a área da marca é o lugar dela.
  const areaDoItem = (i: ItemCarrinho) => (semPrateleira(i) ? null : lugaresDoCarrinho.data?.get(i.product_id)?.area ?? null);
  const temOndeBuscar = (i: ItemCarrinho) => Boolean(areaDoItem(i)) || lugaresDoItem(i).length > 0;
  /** O que a linha do carrinho mostra: "Área Ford" ou o código do lugar principal. */
  const rotuloDoLugar = (i: ItemCarrinho): string | null => {
    const area = areaDoItem(i);
    return area ? `Área ${area.nome}` : lugaresDoItem(i)[0]?.codigo ?? null;
  };
  const naFolha = (i: ItemCarrinho): PecaNaFolha => ({
    product_id: i.product_id, descricao: i.descricao, sku: i.sku,
    // O disponível da linha pode ser o 0 do rascunho (reserva vencida): a folha
    // diria "sem estoque" ao lado da prateleira cheia. Sem saber, não diz (revisão).
    quantidade: null, lugares: lugaresDoItem(i), area: areaDoItem(i),
  });
  const comLugar = itens.filter(temOndeBuscar);
  const diversas = itens.filter(semPrateleira).length;
  const cadastradasSemLugar = itens.filter((i) => !semPrateleira(i) && !temOndeBuscar(i)).length;
  const fraseSemLugar = [
    diversas > 0 ? `${diversas} ${diversas === 1 ? 'peça diversa não tem' : 'peças diversas não têm'} lugar no mapa` : '',
    cadastradasSemLugar > 0
      ? `${cadastradasSemLugar} ${cadastradasSemLugar === 1 ? 'peça cadastrada ainda está' : 'peças cadastradas ainda estão'} sem lugar no mapa`
      : '',
  ].filter(Boolean).join('; ');

  // Na folha aberta pela busca: "Pôr no carrinho" — para quem tocou na faixa
  // achando que era o cartão. Já no carrinho, o recado no lugar do botão (um
  // segundo toque somaria uma unidade sem querer).
  const jaNoCarrinho = (productId: string) => {
    const no = itens.find((i) => !i.linha && i.product_id === productId);
    return no ? `Já está no carrinho (${qtd(no.quantidade)} un).` : null;
  };
  const notaDoMapa = 'O carrinho fica guardado: é só voltar para a Venda.';
  const porNoCarrinho: AcaoNoOndeFica<BuscarPecasRow> = {
    rotulo: 'Pôr no carrinho', fazer: doResultado, jaFeito: jaNoCarrinho, notaDoMapa,
  };
  const porNoCarrinhoDoCatalogo: AcaoNoOndeFica<PecaParaOCarro> = {
    rotulo: 'Pôr no carrinho', fazer: doCatalogo, jaFeito: jaNoCarrinho, notaDoMapa,
  };

  const alterar = (chave: string, campos: Partial<ItemCarrinho>) =>
    setItens((atuais) => atuais.map((i) => (chaveDe(i) === chave ? { ...i, ...campos } : i)));

  const remover = (chave: string) => {
    const alvo = itens.find((i) => chaveDe(i) === chave);
    setItens((atuais) => atuais.filter((i) => chaveDe(i) !== chave));
    // Linha de peça diversa não tem reserva para soltar (ela nunca reservou).
    if (!alvo || alvo.linha) return;
    delete qtdRef.current[chave];
    // Se a rede falhar, não faz mal: a reserva expira sozinha em 30 min.
    soltarCarrinho(chave).catch(() => undefined);
  };

  const desconto = paraNumero(descontoTexto) ?? 0;
  // A TAXA DE ENTREGA (064). Fica FORA do total de peças de propósito: total é
  // peça, e é ele que alimenta comissão, meta, ticket e margem. O que o
  // cliente paga é `totalCobrado`, e é ele que a tela mostra daqui para baixo.
  const entregaTaxa = paraNumero(entregaTexto) ?? 0;
  const bruto = itens.reduce((s, i) => s + i.quantidade * i.preco_unit, 0);
  const total = Math.max(bruto - desconto, 0);
  const totalCobrado = total + entregaTaxa;

  const aoMudarDescontoValor = (t: string) => {
    setDescontoTexto(t);
    origemDesconto.current = t.trim() === '' ? null : 'valor';
    setDescontoPctTexto(numeroParaTexto(valorParaPercentual(paraNumero(t), bruto)));
  };
  const aoMudarDescontoPct = (t: string) => {
    setDescontoPctTexto(t);
    origemDesconto.current = t.trim() === '' ? null : 'pct';
    setDescontoTexto(numeroParaTexto(percentualParaValor(paraNumero(t), bruto)));
  };

  // O carrinho mudou (item entrou, quantidade subiu): o campo que o vendedor
  // digitou por último continua valendo e o espelho refaz a conta. Sem isto,
  // "10%" combinado com o cliente viraria outro percentual assim que mais uma
  // peça entrasse no carrinho.
  useEffect(() => {
    if (origemDesconto.current === 'pct') {
      setDescontoTexto(numeroParaTexto(percentualParaValor(paraNumero(descontoPctTexto), bruto)));
    } else if (origemDesconto.current === 'valor') {
      setDescontoPctTexto(numeroParaTexto(valorParaPercentual(paraNumero(descontoTexto), bruto)));
    }
    // Só o bruto dispara: os textos mudam DENTRO do efeito, e ouvi-los aqui
    // seria um laço de re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bruto]);
  const semPreco = itens.some((i) => i.preco_unit <= 0);
  const acimaDoEstoque = itens.filter((i) => i.quantidade > i.disponivel);
  // Coringa (SKU 9999): a mesma régua que o banco aplica (registrar_venda v6)
  // aplicada ANTES do botão — o vendedor descobre no campo, não no erro.
  const temCoringa = contemCoringa(itens);
  const coringaSemObservacao = !observacaoCobreCoringa(itens, observacao);  // 041: por linha

  // O carrinho sempre à vista (lateral em tela larga, faixa no celular).
  // Resumo derivado do MESMO estado `itens` — visão, não segunda verdade.
  const resumoCarrinho = itens.map((i) => ({
    chave: chaveDe(i),
    descricao: i.descricao,
    descricao_avulsa: i.descricao_avulsa,
    quantidade: i.quantidade,
    preco_unit: i.preco_unit,
    avulsa: Boolean(i.linha),
    lugar: rotuloDoLugar(i),
  }));
  const irAoFechamento = () =>
    document.getElementById('fechamento-da-venda')?.scrollIntoView({ behavior: 'smooth' });

  // "A ver": o crédito abate do total ANTES da forma de pagamento. Quem valida
  // e desconta de verdade é a RPC (com trava de linha contra uso duplo) — aqui
  // é só a prévia do balcão.
  const usoAVer = aVer ? Math.min(Number(aVer.saldo), total) : 0;
  const aPagar = total - usoAVer;
  // 075: o que as PARTES têm de somar — o que a RPC vai lançar no caixa agora:
  // peças (com desconto) + entrega − vale. É o total COBRADO, não o `aPagar`
  // do botão (que não traz a taxa de entrega): registrar_venda confere as
  // partes contra `total_cobrado − a_ver_usado`, e o vale lá cobre a entrega
  // também (`least(saldo, cobrado)`) — a mesma conta aqui, para a tela dizer
  // "fecha" só quando a RPC vai aceitar.
  const aDividir = Math.max(
    totalCobrado - (aVer ? Math.min(Number(aVer.saldo), totalCobrado) : 0), 0);
  const conferencia = conferirPartes(partes, aDividir);
  // 087: crédito sem bandeira e parcelas não fecha. Não é capricho de tela: é
  // o que garante que TODA venda no crédito daqui para a frente saiba quanto
  // rendeu de verdade. A RPC aceita nulo (o orçamento e a encomenda chamam por
  // posição e não sabem de bandeira) — quem obriga é aqui.
  const creditoSemParcelas = modo === 'venda' && forma === 'credito' && !cartao;
  const dividindo = modo === 'venda' && forma === 'misto';
  const divisaoNaoFecha = dividindo && !conferencia.fecha;
  // A sobra do vale nesta venda. O caminho honesto para "usar o vale inteiro"
  // (migração 034) é encerrá-lo abonando a sobra — registrada, com teto para o
  // vendedor. O teto vem de limites_operacao só para AVISAR antes de prometer
  // ao cliente; quem faz valer é a RPC.
  const sobraAVer = aVer ? Math.max(0, Number(aVer.saldo) - usoAVer) : 0;
  // Duas consultas porque são duas coisas: o multiplicador do coringa é regra
  // de PREÇO da loja (vale para todos), e os tetos são de QUEM está vendendo.
  // Antes as duas saíam da mesma linha, com `enabled` amarrado ao abono — o
  // multiplicador só chegava para vendedor com sobra de vale na mão, e todo o
  // resto do balcão precificava a peça diversa no default 2 mesmo quando o
  // dono tinha configurado outro número.
  const limites = useQuery({
    queryKey: ['limites-operacao'],
    queryFn: limitesDaOperacao,
    staleTime: 10 * 60_000,
  });
  // 048: a regra da casa para precificar a peça diversa (custo × N).
  const multCoringa = Number(limites.data?.coringa_multiplicador ?? 2);

  // 087: as taxas do cartão, para a telinha do crédito. `staleTime` longo
  // porque taxa muda por negociação com a adquirente, não por venda — e o que
  // vale de verdade é a que a RPC congela no servidor; isto aqui é para o
  // vendedor VER antes de fechar.
  const taxas = useQuery({
    queryKey: ['taxas-de-cartao', hojeISO()],
    queryFn: () => taxasDeCartao(hojeISO()),
    staleTime: 60 * 60_000,
  });

  // 069: o teto DESTA pessoa — o ajuste da conta dela, senão o do papel. Vem
  // do banco já resolvido, pela mesma função que registrar_venda usa para
  // recusar: é o que impede a tela de prometer um desconto que a RPC corta.
  const meusLimites = useQuery({
    queryKey: ['limites-efetivos'],
    queryFn: () => limitesEfetivos(),
    staleTime: 10 * 60_000,
  });
  const tetoDescontoPct = meusLimites.data ? Number(meusLimites.data.desconto_max_pct) : null;
  const tetoDescontoValor = tetoDescontoPct === null
    ? null : Math.round(bruto * tetoDescontoPct) / 100;
  const descontoAcimaDoTeto = tetoDescontoValor !== null && desconto > tetoDescontoValor;

  const tetoAbono = meusLimites.data?.abono_sobra_max ?? null;
  const abonoBloqueado = tetoAbono !== null && sobraAVer > tetoAbono;

  // O nome do cliente viaja para a reserva ("Luiz reservou 2 para o Fulano").
  // Debounced: digitar um nome não pode virar uma chamada por tecla.
  useEffect(() => {
    if (itens.length === 0) return;
    const t = setTimeout(() => {
      renovarCarrinho(nomeCliente.trim()).then(setExpiraCarrinho).catch(() => undefined);
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nomeCliente]);

  // O relógio da reserva: de 15 em 15 s enquanto houver carrinho.
  useEffect(() => {
    if (!expiraCarrinho || itens.length === 0) return;
    const id = window.setInterval(() => setAgora(Date.now()), 15_000);
    return () => window.clearInterval(id);
  }, [expiraCarrinho, itens.length]);

  const msRestante = expiraCarrinho && itens.length > 0
    ? new Date(expiraCarrinho).getTime() - agora
    : null;
  const minRestante = msRestante !== null ? Math.max(0, Math.ceil(msRestante / 60_000)) : null;

  // Expirou: o banco já soltou as reservas; a tela conta a mesma história em
  // vez de fingir que o carrinho ainda segura alguma coisa.
  useEffect(() => {
    if (msRestante !== null && msRestante <= 0) {
      setItens([]);
      qtdRef.current = {};
      setExpiraCarrinho(null);
      setErroCarrinho('O carrinho ficou 30 min parado, expirou, e as peças foram '
        + 'liberadas para os colegas. É só montar de novo.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msRestante]);

  const venda = useMutation({
    mutationFn: () => registrarVenda({
      itens: itens.map((i) => ({
        product_id: i.product_id, quantidade: i.quantidade, preco_unit: i.preco_unit,
        // 041: o que saiu nesta linha (o banco só usa isto no coringa).
        descricao: i.descricao_avulsa?.trim() || undefined,
        // 048: o custo digitado da diversa — vira o CMV desta linha.
        custo: i.custo_avulso ?? undefined,
        // 071: o código, que fica no sistema e não no papel.
        codigo: i.codigo_avulso?.trim() || undefined,
        // 073: o carro desta linha, só quando é OUTRO que não o da venda.
        veiculo_montadora: i.veiculo_montadora?.trim() || undefined,
        veiculo_modelo: i.veiculo_modelo?.trim() || undefined,
      })),
      forma,
      // 087: a bandeira e as vezes so viajam no credito, pela mesma razao das
      // partes logo abaixo: a RPC recusa bandeira em venda de dinheiro.
      cartaoBandeira: forma === 'credito' ? (cartao?.bandeira ?? null) : null,
      cartaoParcelas: forma === 'credito' ? (cartao?.parcelas ?? null) : null,
      // 075: as partes só viajam com 'misto' — a RPC recusa qualquer outra
      // combinação, e é melhor a tela nunca a produzir.
      pagamentos: forma === 'misto' ? partesParaRpc(partes) : null,
      desconto,
      entregaTaxa,
      cliente: nomeCliente.trim() || null,
      observacao: observacao.trim() || null,
      aVerId: aVer?.id ?? null,
      abonarSobra: aVer ? (abonarSobra && !abonoBloqueado) : false,
      chave: chaveVenda,
      customerId: clienteId,
      // Meio carro não vai: o banco recusa, e aqui nem chega a sair. O ano só
      // acompanha quando há carro — ano solto não diz nada (lib/carro.ts).
      ...carroParaRpc(carro),
    }),
    onSuccess: (saleId) => {
      // Venda fechada: o rascunho cumpriu o papel dele e sai da frente (senão
      // o próximo atendimento abriria com o carrinho do anterior).
      limparRascunho(chaveRascunho);
      setConcluida(saleId);
      setVendido(itens);
      setChaveVenda(crypto.randomUUID());
      setItens([]);
      setDescontoTexto('');
      setNomeCliente('');
      setClienteId(null);
      setObservacao('');
      setCarro(CARRO_VAZIO);
      setAVer(null);
      setCodigoAVer('');
      setAbonarSobra(false);
      setPartes(PARTES_INICIAIS);
      // As reservas já foram apagadas pela própria RPC (carrinho virou venda).
      qtdRef.current = {};
      setExpiraCarrinho(null);
      setErroCarrinho(null);
      cliente.invalidateQueries({ queryKey: ['buscar'] });
      cliente.invalidateQueries({ queryKey: ['caixa'] });
      cliente.invalidateQueries({ queryKey: ['reposicao'] });
      cliente.invalidateQueries({ queryKey: ['a-ver'] });
    },
  });

  // Orçamento: os MESMOS itens e desconto, mas nada acontece no banco além do
  // documento — o cliente leva o papel (ou o WhatsApp) e volta depois.
  // Os carros do cliente escolhido. Só busca quando há cliente CADASTRADO e a
  // tela está em modo orçamento — no balcão comum isso não interessa.
  const carrosDoCliente = useQuery({
    queryKey: ['veiculos', clienteId],
    queryFn: () => veiculosDoCliente(clienteId!),
    enabled: Boolean(clienteId) && orcando,
  });

  const orcamento = useMutation({
    mutationFn: () => emitirOrcamento({
      itens: itens.map((i) => ({
        product_id: i.product_id, quantidade: i.quantidade, preco_unit: i.preco_unit,
        // O papel do cliente diz o NOME da peça diversa, não "PEÇA DIVERSA —
        // BALCÃO" cinco vezes (17/08/2026). A RPC congela essa descrição.
        descricao: i.descricao_avulsa?.trim() || undefined,
        // 071: o código vai junto e fica congelado no orçamento — mas some do
        // papel e do PDF, como o sku já sumia desde 18/08/2026.
        codigo: i.codigo_avulso?.trim() || undefined,
      })),
      desconto,
      clienteNome: nomeCliente.trim() || null,
      clienteContato: contatoCliente.trim() || null,
      validoAte: validade || null,
      canal: canalOrc,
      veiculo: veiculoOrc.trim() || null,
      observacao: obsOrc.trim() || null,
      customerId: clienteId,
      customerVehicleId: carroOrc || null,
    }),
    onSuccess: ({ quote_id }) => {
      // O carrinho virou papel, não venda: as peças voltam a estar livres — o
      // orçamento nunca reservou estoque, e a reserva era do ATENDIMENTO.
      limparRascunho(chaveRascunho);
      qtdRef.current = {};
      soltarCarrinho().catch(() => undefined);
      navigate(`/orcamentos/${quote_id}`);
    },
  });

  // ATUALIZAR PREÇO (19/08/2026, pedido do dono): quando a GERÊNCIA vende por
  // um valor diferente do catálogo, o catálogo é que costuma estar velho — e
  // quem descobre isso é o balcão, na hora. Um toque acerta para todas as
  // próximas vendas. Não confundir com desconto: desconto vale para ESTA
  // venda; isto muda a tabela.
  const podeAcertarPreco = podeGerenciarCatalogo(papel);
  const acertarPreco = useMutation({
    mutationFn: (p: { productId: string; preco: number }) =>
      atualizarPrecoBalcao(p.productId, p.preco),
    onSuccess: () => {
      cliente.invalidateQueries({ queryKey: ['buscar'] });
      cliente.invalidateQueries({ queryKey: ['peca'] });
    },
  });

  const procurarAVer = useMutation({
    mutationFn: async () => {
      const achados = await acharAVer(codigoAVer);
      const valido = achados.find((c) => !c.cancelado_em && Number(c.saldo) > 0
        && (!c.valido_ate || c.valido_ate >= hojeISO()));
      if (!valido) {
        throw new Error(achados.length
          ? 'Encontrado, mas sem saldo disponível (usado, vencido ou cancelado).'
          : 'Nenhum "a ver" com esse código ou nome.');
      }
      setAVer(valido);
    },
  });

  return (
    <Layout titulo={modo === 'orcamento' ? 'Orçamento' : 'Venda rápida'}>
      {/* Em tela larga, duas colunas: a busca à esquerda e o CARRINHO fixo à
          direita (pedido do dono: item adicionado "ia lá para o final"). No
          celular o fluxo vertical fica como sempre foi — muda só a faixa fixa
          logo acima do nav. */}
      <div className="lg:grid lg:grid-cols-[1fr_21rem] lg:items-start lg:gap-5">
      <div className="space-y-5">
        {concluida && (
          <Aviso tipo="ok">
            Venda registrada. Estoque baixado e entrada lançada no caixa.
            <span className="numero ml-1 text-xs opacity-70">#{concluida.slice(0, 8)}</span>
            {vendido.length > 0 && (
              <ul className="mt-2 space-y-0.5 text-xs">
                {vendido.map((i, n) => (
                  <li key={`${i.product_id}-${i.linha ?? n}`} className="flex justify-between gap-3">
                    {/* A PEÇA DIVERSA aparece pelo que o vendedor escreveu, não
                        como "PEÇA DIVERSA — BALCÃO": é a única informação que
                        existe sobre ela, e some do carrinho no instante seguinte. */}
                    <span className="min-w-0">
                      {qtd(i.quantidade)}× {nomeDoItemVendido(i)}
                      {i.linha ? <span className="ml-1 uppercase opacity-70">diversa</span> : null}
                    </span>
                    <span className="numero shrink-0">{brl(i.quantidade * i.preco_unit)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Aviso>
        )}

        {/* Os três modos da Busca, aqui com um destino só: o carrinho. O
            seletor é o mesmo da tela de Busca (Grafite): o ativo é o degrau de
            cinza, e o alvo tem 44 px no celular. */}
        <div className="grid grid-cols-3 gap-1 rounded-[0.625rem] border border-fio bg-panel p-[3px] sm:grid-cols-5">
          {MODOS_BUSCA.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setModoBusca(m.id)}
              aria-pressed={modoBusca === m.id}
              className={[
                'min-h-11 rounded-lg px-3 text-sm font-medium transition-colors sm:min-h-9',
                modoBusca === m.id ? 'bg-edge text-paper' : 'text-mute hover:text-paper',
              ].join(' ')}
            >
              {m.rotulo}
            </button>
          ))}
        </div>

        {/* aoBiparExato: bipou o código, a peça cai no carrinho sem clique. */}
        {modoBusca === 'livre' && (
          <BuscaPecas
            onEscolher={doResultado}
            aoBiparExato={doResultado}
            acaoNoOndeFica={porNoCarrinho}
            placeholder="Bipe o código ou digite a peça…"
          />
        )}
        {modoBusca === 'tipo' && (
          <NavegarCatalogo key="tipo" comeco="tipo" onEscolher={doCatalogo} acaoNoOndeFica={porNoCarrinhoDoCatalogo} />
        )}
        {modoBusca === 'carro' && (
          <NavegarCatalogo key="carro" comeco="carro" onEscolher={doCatalogo} acaoNoOndeFica={porNoCarrinhoDoCatalogo} />
        )}
        {modoBusca === 'veiculo' && <BuscaGuiada onEscolher={doResultado} acaoNoOndeFica={porNoCarrinho} />}
        {modoBusca === 'placa' && <BuscaPlaca onEscolher={doResultado} acaoNoOndeFica={porNoCarrinho} />}

        {/* A VÁLVULA DO BALCÃO, num toque (08/10/2026). Fica FORA do seletor de
            modo de propósito: ela não é um jeito de procurar: é o que se faz
            quando procurar não achou, e em qualquer um dos cinco modos a
            resposta é a mesma. Some se o coringa não estiver cadastrado —
            botão que falha na cara do vendedor é pior do que botão nenhum. */}
        {coringa.data && (
          <div>
            <button type="button" className="botao-secundario w-full"
                    onClick={adicionarPecaDiversa}>
              + Peça diversa
            </button>
            <p className="mt-1 text-center text-xs text-dim">
              para o que ainda não está cadastrado — você escreve o que saiu e o preço
            </p>
          </div>
        )}

        {erroCarrinho && <Aviso tipo="alerta">{erroCarrinho}</Aviso>}

        {itens.length > 0 && (
          <section className="cartao p-4">
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
              <h2 className="rotulo">Itens da venda</h2>
              {minRestante !== null && (
                <span className="flex items-center gap-1.5 text-xs text-dim">
                  {minRestante <= 5 && <span className="ponto text-alerta" aria-hidden="true" />}
                  reservado para você · <span className="numero">{minRestante}</span> min
                </span>
              )}
              {/* Com 2+ peças com lugar, a volta pela loja numa lista só, na
                  ordem da caminhada (06/10/2026). */}
              {comLugar.length >= 2 && (
                <button type="button" onClick={() => setOndeFica({ tipo: 'lista' })}
                        className="botao-secundario min-h-11 w-full px-3 py-2 text-sm sm:w-auto">
                  Onde buscar tudo ›
                </button>
              )}
            </div>
            {minRestante !== null && minRestante <= 5 && (
              <div className="mb-2 flex items-center justify-between gap-3 rounded-[0.625rem] border border-edge bg-panel-alt p-2">
                <p className="flex items-start gap-2 text-xs text-mute">
                  <span className="ponto mt-1 text-alerta" aria-hidden="true" />
                  A reserva está acabando — depois disso as peças ficam livres para os colegas.
                </p>
                <button type="button" className="botao-secundario shrink-0 px-3 py-1.5 text-xs"
                        onClick={() => {
                          renovarCarrinho(nomeCliente.trim())
                            .then(setExpiraCarrinho).catch(() => undefined);
                        }}>
                  Manter por mais 30 min
                </button>
              </div>
            )}
            <ul className="divide-y divide-fio">
              {itens.map((i) => {
                const chave = chaveDe(i);
                const avulsa = Boolean(i.linha);
                // Peça diversa não tem tipo, logo nunca tem jogo.
                const unidadesJogo = avulsa ? null : unidadesDoJogo(tiposDoCarrinho.data?.get(i.product_id));
                return (
                <li key={chave} className="py-3">
                  <div className="flex items-start justify-between gap-3">
                    {/* A foto da peça, sem o fundo branco (01/10/2026); a
                        peça diversa e o coringa não têm foto própria. */}
                    {!avulsa && i.sku !== SKU_CORINGA && (
                      <MiniaturaDaPeca productId={i.product_id} descricao={i.descricao} tamanho={44}
                                       fotos={(fotosDoCarrinho.data ?? []).filter((f) => f.product_id === i.product_id)} />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">{i.descricao}</p>
                      <p className="numero text-xs text-dim">{i.sku}</p>
                      {/* Onde buscar: o código do lugar abre a folha por cima
                          do carrinho, sem sair da venda. */}
                      {rotuloDoLugar(i) && (
                        <button type="button" onClick={() => setOndeFica({ tipo: 'peca', chave })}
                                aria-label={`Onde fica: ${rotuloDoLugar(i)}`}
                                className={`inline-flex min-h-[44px] items-center gap-1 text-sm font-medium text-signal-soft ${areaDoItem(i) ? '' : 'numero'}`}>
                          {rotuloDoLugar(i)}
                          {!areaDoItem(i) && lugaresDoItem(i).length > 1 && (
                            <span className="font-sans text-xs font-normal text-dim">+{lugaresDoItem(i).length - 1}</span>
                          )}
                          <span aria-hidden="true">›</span>
                        </button>
                      )}
                    </div>
                    <div className="flex shrink-0 flex-col items-end text-right">
                      <p className="numero text-paper">{brl(i.quantidade * i.preco_unit)}</p>
                      {/* Tirar do carrinho é texto (terciário): o vermelho é do
                          erro, e a peça volta com um bipe. */}
                      <button
                        type="button"
                        onClick={() => remover(chave)}
                        className="inline-flex min-h-11 items-center text-xs text-mute underline hover:text-paper sm:min-h-0"
                        aria-label={`Remover ${i.descricao}`}
                      >
                        remover
                      </button>
                    </div>
                  </div>

                  {/* PEÇA DIVERSA (041): cada linha diz o que É. Sem isto, cinco
                      peças diferentes viravam cinco unidades da mesma coisa a um
                      preço só — e a auditoria de amanhã não sabia o que saiu. */}
                  {avulsa && (
                    <div className="mt-2 grid grid-cols-[1fr_7rem] gap-3">
                      <div className="min-w-0">
                        <label className="rotulo" htmlFor={`avulsa-${chave}`}>
                          Que peça é esta?
                        </label>
                        <input
                          id={`avulsa-${chave}`}
                          className="campo py-2.5"
                          value={i.descricao_avulsa ?? ''}
                          placeholder="Ex.: Retrovisor Uno 98 LD"
                          onChange={(e) => alterar(chave, { descricao_avulsa: e.target.value })}
                        />
                        {/* O CÓDIGO TEM CAMPO PRÓPRIO (071). Antes ele era
                            escrito aqui em cima, no nome — e o nome é o que
                            sai no recibo. O cliente lia o código da peça, que
                            é informação nossa. Agora o nome fica limpo e o
                            código fica no sistema. */}
                        <label className="rotulo mt-2 block" htmlFor={`codigo-${chave}`}>
                          Código (só para nós)
                        </label>
                        <input
                          id={`codigo-${chave}`}
                          className="campo py-2.5 font-mono"
                          value={i.codigo_avulso ?? ''}
                          placeholder="Ex.: 9.4212.20"
                          autoCapitalize="characters"
                          autoComplete="off"
                          onChange={(e) => alterar(chave, { codigo_avulso: e.target.value })}
                        />
                        <p className="mt-1 text-xs text-dim">
                          Não sai no recibo nem no orçamento do cliente.
                        </p>
                      </div>
                      {/* O custo digitado SUGERE o preço (custo × regra da casa,
                          048) — a conta que o vendedor fazia de cabeça. O preço
                          continua editável; digitar o custo de novo re-sugere. */}
                      <div>
                        <label className="rotulo" htmlFor={`custo-${chave}`}>Custo (R$)</label>
                        <CampoNumero
                          id={`custo-${chave}`}
                          className="campo py-2.5"
                          valor={i.custo_avulso ?? null}
                          placeholder="0,00"
                          onValor={(n) => alterar(chave, {
                            custo_avulso: n,
                            ...(n !== null && n > 0
                              ? { preco_unit: Math.round(n * multCoringa * 100) / 100 }
                              : {}),
                          })}
                        />
                      </div>
                    </div>
                  )}

                  {/* Quantidade e preço lado a lado, cada um com largura própria:
                      no celular de 360px o campo de preço não pode encolher a
                      ponto de esconder o valor que está sendo digitado. */}
                  <div className="mt-2 flex items-end gap-3">
                    <div>
                      <span className="rotulo">Qtd.</span>
                      <div className="flex items-center rounded-[0.625rem] border border-edge-forte">
                        <button
                          type="button"
                          className="min-h-11 min-w-11 rounded-l-[0.625rem] text-lg text-signal-soft hover:bg-panel-alt"
                          onClick={() => mudarQuantidade(chave, Math.max(1, i.quantidade - 1), avulsa)}
                          aria-label="Diminuir quantidade"
                        >−</button>
                        <CampoNumero
                          className="numero w-12 border-x border-edge bg-transparent py-2.5 text-center"
                          valor={i.quantidade}
                          // Campo vazio (null) é ignorado de propósito: não
                          // existe item de zero unidades, e o valor anterior
                          // volta sozinho quando o campo perde o foco.
                          onValor={(n) => { if (n !== null) mudarQuantidade(chave, n, avulsa); }}
                          aria-label="Quantidade"
                        />
                        <button
                          type="button"
                          className="min-h-11 min-w-11 rounded-r-[0.625rem] text-lg text-signal-soft hover:bg-panel-alt"
                          onClick={() => mudarQuantidade(chave, i.quantidade + 1, avulsa)}
                          aria-label="Aumentar quantidade"
                        >+</button>
                      </div>
                    </div>

                    <div className="min-w-0 flex-1">
                      <label className="rotulo" htmlFor={`preco-${chave}`}>Preço un. (R$)</label>
                      <CampoNumero
                        id={`preco-${chave}`}
                        className="campo py-2.5"
                        valor={i.preco_unit === 0 ? null : i.preco_unit}
                        placeholder="0,00"
                        onValor={(n) => alterar(chave, { preco_unit: n ?? 0 })}
                      />
                    </div>
                  </div>

                  {/* O JOGO COMPLETO, NUM TOQUE (08/10/2026, pedido do dono).
                      Até hoje a calota era vendida em jogo; o estoque virou
                      PEÇA (× 4) e o preço ÷ 4, então quem leva o jogo depende
                      de o vendedor digitar 4 — e se ele deixar 1, a loja cobra
                      um quarto. O botão fixa a quantidade em 4 (absoluta, não
                      soma): dois toques nervosos continuam sendo um jogo.
                      Em fileira própria, e não ao lado da quantidade: a 375 px
                      o campo de preço já está no limite. */}
                  {unidadesJogo !== null && (
                    <BotaoJogo
                      unidades={unidadesJogo}
                      quantidade={i.quantidade}
                      descricao={i.descricao}
                      aoTocar={() => mudarQuantidade(chave, unidadesJogo, false)}
                    />
                  )}

                  {/* 073 — O CARRO DESTA LINHA. Só aparece escrito quando é
                      OUTRO carro: o normal é a linha herdar o carro da venda,
                      e repetir o mesmo carro cinco vezes seria trabalho sem
                      dado novo. O mecânico que leva pastilha do Onix e
                      limitador do Sandero na mesma venda é quem usa isto. */}
                  {i.veiculo_modelo !== undefined ? (
                    <div className="mt-2 rounded-[0.625rem] border border-edge p-2">
                      <div className="mb-1 flex items-center justify-between gap-2">
                        <span className="rotulo">Carro desta peça</span>
                        <button
                          type="button"
                          className="inline-flex min-h-11 items-center text-xs text-mute underline hover:text-paper sm:min-h-0"
                          onClick={() => alterar(chave, {
                            veiculo_montadora: undefined, veiculo_modelo: undefined,
                            veiculo_texto: undefined,
                          })}
                        >
                          usar o carro da venda
                        </button>
                      </div>
                      {/* O mesmo campo do carro da venda, sem ano (sale_items
                          não tem): "onix 1.0" basta. Os botões vêm do texto
                          desta linha. */}
                      <SeletorVeiculo
                        valor={{
                          montadora: i.veiculo_montadora ?? '',
                          modelo: i.veiculo_modelo ?? '',
                          ano: '',
                          veiculoId: null,
                          texto: i.veiculo_texto,
                        }}
                        aoTrocar={(c) => alterar(chave, {
                          veiculo_montadora: c.montadora,
                          veiculo_modelo: c.modelo,
                          veiculo_texto: c.texto,
                        })}
                        idPrefixo={`carro-linha-${chave}`}
                        rotulo=""
                        textos={[i.descricao_avulsa ?? i.descricao]}
                        linha
                      />
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="mt-2 inline-flex min-h-11 items-center text-xs text-mute underline hover:text-paper sm:min-h-0"
                      // Abre o bloco VAZIO. Até 08/10/2026 ele abria com a
                      // montadora da venda já posta (atalho de quando eram
                      // duas listas); com o campo que lê o modelo, a montadora
                      // pré-escrita atrapalha: "uno" num campo que já dizia
                      // "VolksWagen" viraria um Uno da VW. String vazia — e
                      // não undefined — é o que diz "esta linha tem carro
                      // próprio, ainda por escolher".
                      onClick={() => alterar(chave, {
                        veiculo_montadora: '',
                        veiculo_modelo: '',
                        veiculo_texto: '',
                      })}
                    >
                      {carroPreenchido(carro)
                        ? `Esta peça é de outro carro (não ${textoDoCarro({
                          montadora: carro.montadora, modelo: carro.modelo })})`
                        : 'Esta peça é de um carro específico'}
                    </button>
                  )}

                  {/* ATUALIZAR PREÇO (gerência): o valor praticado divergiu do
                      catálogo. Quem vê primeiro que a tabela envelheceu é o
                      balcão — aqui isso vira um toque, em vez de "depois eu
                      arrumo" que nunca chega. */}
                  {!avulsa && podeAcertarPreco && i.preco_tabela != null
                    && Math.abs(i.preco_unit - i.preco_tabela) > 0.005
                    && i.preco_unit > 0 && (
                    <div className="mt-2 flex flex-wrap items-center gap-2 rounded-[0.625rem] border border-edge bg-panel-alt p-2">
                      <p className="flex min-w-0 flex-1 items-start gap-2 text-xs text-mute">
                        <span className="ponto mt-1 text-alerta" aria-hidden="true" />
                        <span>
                          No sistema esta peça está <strong className="numero font-medium text-paper">{brl(i.preco_tabela)}</strong>,
                          e você está vendendo por <strong className="numero font-medium text-paper">{brl(i.preco_unit)}</strong>.
                        </span>
                      </p>
                      <button
                        type="button"
                        className="botao-secundario min-h-11 shrink-0 px-3 py-1.5 text-xs sm:min-h-0"
                        disabled={acertarPreco.isPending}
                        onClick={() => {
                          acertarPreco.mutate({ productId: i.product_id, preco: i.preco_unit });
                          // A referência muda junto: o aviso some, e uma
                          // segunda troca de preço volta a aparecer.
                          alterar(chave, { preco_tabela: i.preco_unit });
                        }}
                      >
                        Atualizar preço no sistema
                      </button>
                    </div>
                  )}
                  {acertarPreco.error ? (
                    <p className="mt-1 text-xs text-erro">{mensagemDeErro(acertarPreco.error)}</p>
                  ) : null}

                  {!avulsa && i.quantidade > i.disponivel && (
                    <p className="mt-1 flex items-start gap-2 text-xs text-mute">
                      <span className="ponto mt-1 text-alerta" aria-hidden="true" />
                      <span>
                        Estoque registrado: <span className="numero">{qtd(i.disponivel)}</span> — a venda passa e a diferença
                        fica como pendente de registro até a entrada da mercadoria ser lançada.
                      </span>
                    </p>
                  )}
                </li>
                );
              })}
            </ul>
          </section>
        )}

        {itens.length > 0 && (
          <section id="fechamento-da-venda" className="cartao space-y-4 p-4">
            {modo === 'venda' && (
            <div>
              <p className="rotulo">Forma de pagamento</p>
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                {FORMAS.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => {
                      setForma(f.id);
                      // Trocou a forma: a escolha do cartao morre. Sem isto, a
                      // bandeira de uma tentativa de credito viajaria numa
                      // venda em dinheiro (a RPC recusa, mas a tela nao pode
                      // produzir isso).
                      setCartao(null);
                      // 087: e o credito ABRE a telinha na hora. O dono pediu
                      // que ela venha sozinha - escolher a forma e escolher em
                      // quantas vezes sao o mesmo gesto no balcao.
                      if (f.id === 'credito') setEscolhendoParcelas(true);
                    }}
                    aria-pressed={forma === f.id}
                    className={botaoDeEscolha(forma === f.id)}
                  >
                    {f.rotulo}
                  </button>
                ))}
              </div>

              {/* 087: o que foi escolhido na telinha, resumido. Fica à vista
                  porque o vendedor precisa poder conferir (e trocar) antes de
                  confirmar — a escolha some no meio da tela, e errar a
                  bandeira muda a taxa de 2,55% para 5,96%. */}
              {forma === 'credito' && cartao && (
                <button type="button"
                        className="mt-3 flex min-h-11 w-full items-center justify-between gap-3 rounded-[0.625rem] border border-edge-forte bg-panel-alt p-3 text-left"
                        onClick={() => setEscolhendoParcelas(true)}>
                  <span>
                    <span className="block text-sm font-medium">
                      {NOME_DA_BANDEIRA[cartao.bandeira]} · <span className="numero">{cartao.parcelas}x</span>
                    </span>
                    <span className="block text-xs text-dim">toque para trocar</span>
                  </span>
                  {(() => {
                    const taxa = taxaDaFaixa(
                      taxas.data ?? [], cartao.bandeira, 'credito', cartao.parcelas);
                    // Sem taxa carregada a tela não INVENTA zero: fica calada,
                    // e quem decide de verdade é a RPC, que recusa.
                    if (taxa === null) return null;
                    return (
                      <span className="shrink-0 text-right">
                        <span className="block text-[0.6875rem] text-dim">loja recebe</span>
                        <span className="numero block text-sm">
                          {brl(Math.round(totalCobrado * (100 - taxa)) / 100)}
                        </span>
                      </span>
                    );
                  })()}
                </button>
              )}

              {/* 075: AS PARTES. Aparecem só com "Dividir" ligado. A conta é a
                  de lib/pagamentos (em centavos, como a RPC compara) — a tela
                  diz "fecha" exatamente quando o banco vai aceitar. */}
              {dividindo && (
                <div className="mt-3 space-y-2 rounded-[0.625rem] border border-edge-forte bg-panel-alt p-3">
                  <p className="text-xs text-mute">
                    Quanto em cada forma. As partes têm de somar{' '}
                    <strong className="numero font-medium text-paper">{brl(aDividir)}</strong>
                    {usoAVer > 0 ? ' (já descontado o vale)' : ''}. Cada parte entra
                    no caixa pela sua forma — o troco é sobre a parte em dinheiro.
                  </p>
                  {partes.map((p, i) => (
                    <div key={i} className="grid grid-cols-[1fr_1fr_auto] items-end gap-2">
                      <div>
                        <label className="rotulo" htmlFor={`parte-forma-${i}`}>Forma</label>
                        <select id={`parte-forma-${i}`} className="campo py-2" value={p.forma}
                                onChange={(e) => setPartes(partes.map((x, j) => (
                                  j === i ? { ...x, forma: e.target.value as PaymentMethod } : x)))}>
                          {FORMAS_QUE_DIVIDEM.map((f) => (
                            <option key={f} value={f}>{rotuloDaForma(f)}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="rotulo" htmlFor={`parte-valor-${i}`}>Valor (R$)</label>
                        <input id={`parte-valor-${i}`} className="campo py-2" inputMode="decimal"
                               placeholder="0,00" value={p.valorTexto}
                               onChange={(e) => setPartes(partes.map((x, j) => (
                                 j === i ? { ...x, valorTexto: e.target.value } : x)))} />
                      </div>
                      <div className="flex gap-1">
                        <button type="button" title="Preencher com o que falta"
                                className="min-h-11 rounded-lg border border-edge-forte px-2.5 text-xs font-medium hover:bg-edge"
                                onClick={() => setPartes(partes.map((x, j) => (
                                  j === i ? { ...x, valorTexto: restanteDaParte(partes, i, aDividir) } : x)))}>
                          resto
                        </button>
                        {partes.length > 2 && (
                          <button type="button" title="Tirar esta parte"
                                  className="min-h-11 min-w-11 rounded-lg border border-edge-forte text-xs text-mute hover:bg-edge hover:text-paper"
                                  onClick={() => setPartes(partes.filter((_, j) => j !== i))}>
                            ×
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <button type="button" className="inline-flex min-h-11 items-center text-xs text-signal-soft underline"
                            onClick={() => setPartes([...partes, { forma: 'pix', valorTexto: '' }])}>
                      + outra parte
                    </button>
                    <p className={`numero text-xs font-medium ${conferencia.fecha ? 'text-ok' : 'text-erro'}`}>
                      {conferencia.fecha
                        ? 'Fecha ✓'
                        : conferencia.incompleta
                          ? 'Preencha o valor de cada parte'
                          : conferencia.diferenca > 0
                            ? `Faltam ${brl(conferencia.diferenca)}`
                            : `Sobram ${brl(-conferencia.diferenca)}`}
                    </p>
                  </div>
                </div>
              )}
            </div>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="rotulo" htmlFor="desconto">Desconto (R$)</label>
                    <input
                      id="desconto"
                      className="campo"
                      inputMode="decimal"
                      placeholder="0,00"
                      value={descontoTexto}
                      onChange={(e) => aoMudarDescontoValor(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="rotulo" htmlFor="desconto-pct">Desconto (%)</label>
                    <input
                      id="desconto-pct"
                      className="campo"
                      inputMode="decimal"
                      placeholder="0"
                      value={descontoPctTexto}
                      onChange={(e) => aoMudarDescontoPct(e.target.value)}
                    />
                  </div>
                </div>
                <p className="mt-1 text-xs text-mute">
                  Preencha um e o outro se ajusta — o total já sai com o abate.
                </p>

                {/* O TETO, ANTES DA RECUSA (069). Quem valida é registrar_venda;
                    isto existe para o vendedor não prometer ao cliente um
                    desconto que a venda vai cortar — descobrir a trava com o
                    cliente na frente é pior que a trava. O número é o EFETIVO:
                    quando o dono ajusta a conta desta pessoa, é o dela que
                    aparece aqui, não o do papel. */}
                {tetoDescontoPct !== null && tetoDescontoPct < 100 && bruto > 0 && (
                  <p className={`mt-1 text-xs ${descontoAcimaDoTeto ? 'text-erro' : 'text-dim'}`}>
                    {descontoAcimaDoTeto
                      ? `Seu limite é ${tetoDescontoPct}% — nesta venda, até ${brl(tetoDescontoValor ?? 0)}. Acima disso a venda é recusada.`
                      : `Você pode dar até ${tetoDescontoPct}% — nesta venda, ${brl(tetoDescontoValor ?? 0)}.`}
                  </p>
                )}

                {/* A ENTREGA A DOMICÍLIO (064, pedido do dono). Campo de
                    largura cheia: três inputs lado a lado não cabem em 360px.
                    O endereço se combina depois, na aba Vendas — aqui é só o
                    dinheiro, que é o que precisa entrar antes de fechar. */}
                <div className="mt-3">
                  <label className="rotulo" htmlFor="entrega-taxa">
                    Entrega a domicílio (R$)
                  </label>
                  <input
                    id="entrega-taxa"
                    className="campo"
                    inputMode="decimal"
                    placeholder="0,00"
                    value={entregaTexto}
                    onChange={(e) => setEntregaTexto(e.target.value)}
                  />
                  <p className="mt-1 text-xs text-mute">
                    Soma no total e sai em linha própria no recibo. O endereço
                    se combina depois, em Vendas.
                  </p>
                </div>
              </div>
              <SeletorCliente
                clienteId={clienteId}
                clienteNome={nomeCliente}
                aoEscolher={({ id, nome }) => { setClienteId(id); setNomeCliente(nome); }}
              />
            </div>

            {/* 073 — DE QUE CARRO É a peça. Até 14/09/2026 só o orçamento
                perguntava isso, e o relatório de carros do mês tinha de ser
                adivinhado lendo a descrição digitada. Opcional: não trava
                venda nenhuma. Desde 08/10/2026 o carro citado no texto das
                peças diversas ("LÂMPADA H4 GOL") aparece como botão — um
                toque preenche; sozinho, nunca. */}
            {modo === 'venda' && (
              <SeletorVeiculo
                valor={carro}
                aoTrocar={setCarro}
                clienteId={clienteId}
                idPrefixo="carro-venda"
                rotulo="Carro (opcional)"
                textos={itens.map((i) => i.descricao_avulsa)}
              />
            )}

            {modo === 'venda' && (
            <div>
              {/* OBSERVAÇÃO, e só. Ela já foi "Qual peça saiu?" quando havia
                  PEÇA DIVERSA no carrinho, de uma época em que a descrição da
                  peça só cabia aqui. Desde a 041 cada linha diversa tem o
                  campo dela ("Que peça é esta?"), que é o que vai para o banco
                  e para o recibo — este aqui perguntar a mesma coisa fazia o
                  vendedor escrever duas vezes, ou escrever aqui e deixar a
                  linha muda. Agora é o que o dono pediu: o lugar de quem
                  atendeu deixar um recado sobre a venda.

                  EM DESTAQUE porque é o único campo da tela que ninguém lê
                  depois por obrigação: quem escreve aqui está avisando alguém.
                  Era vermelho até 28/10/2026; no Grafite o destaque é o claro
                  (`text-signal`), e o vermelho fica para o erro.
                  A trava da PEÇA DIVERSA não mudou — a venda continua sem
                  fechar enquanto uma linha diversa estiver sem descrição, e o
                  aviso aparece na linha, onde se resolve. */}
              <label className="rotulo text-signal" htmlFor="observacao">
                Observação (opcional)
              </label>
              <input
                id="observacao"
                className="campo"
                value={observacao}
                onChange={(e) => setObservacao(e.target.value)}
                placeholder="Algum recado sobre esta venda?"
              />
            </div>
            )}

            {/* "A ver": cliente apresenta o vale, o vendedor digita o código */}
            {modo === 'venda' && (
            <div className="rounded-[0.625rem] border border-edge p-3">
              <p className="rotulo">Vale "a ver"</p>
              {!aVer ? (
                <div className="flex gap-2">
                  <input
                    className="campo py-2 font-mono"
                    placeholder="AV-000123 ou nome do cliente"
                    value={codigoAVer}
                    onChange={(e) => setCodigoAVer(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') procurarAVer.mutate(); }}
                  />
                  <button type="button" className="botao-secundario min-h-11 shrink-0 py-2"
                          disabled={procurarAVer.isPending || !codigoAVer.trim()}
                          onClick={() => procurarAVer.mutate()}>
                    {procurarAVer.isPending ? '…' : 'Aplicar'}
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="numero text-sm text-paper">{aVer.codigo}</p>
                    <p className="text-xs text-mute">
                      {aVer.cliente_nome} · saldo <span className="numero">{brl(Number(aVer.saldo))}</span>
                    </p>
                  </div>
                  <button type="button" className="inline-flex min-h-11 items-center px-2 text-sm text-mute underline hover:text-paper"
                          onClick={() => { setAVer(null); setCodigoAVer(''); setAbonarSobra(false); }}>
                    remover
                  </button>
                </div>
              )}
              {procurarAVer.error ? (
                <p className="mt-2 text-xs text-erro">{mensagemDeErro(procurarAVer.error)}</p>
              ) : null}
            </div>
            )}

            <div className="space-y-1 border-t border-edge pt-4">
              {usoAVer > 0 && (
                <>
                  <div className="flex items-baseline justify-between text-sm">
                    <span className="text-dim">Total dos itens</span>
                    <span className="numero text-mute">{brl(totalCobrado)}</span>
                  </div>
                  {/* O abate do vale é conta, não sinal: neutro (Grafite). */}
                  <div className="flex items-baseline justify-between text-sm">
                    <span className="text-dim">Vale {aVer?.codigo}</span>
                    <span className="numero text-mute">−{brl(usoAVer)}</span>
                  </div>
                  {sobraAVer > 0 && (
                    <div className="rounded-[0.625rem] border border-edge p-2">
                      <label className="flex items-start gap-2 text-xs">
                        <input
                          type="checkbox"
                          className="mt-0.5"
                          checked={abonarSobra && !abonoBloqueado}
                          disabled={abonoBloqueado}
                          onChange={(e) => setAbonarSobra(e.target.checked)}
                        />
                        <span>
                          Encerrar o vale: o cliente abre mão da sobra de{' '}
                          <strong>{brl(sobraAVer)}</strong> — fica registrado.
                          {!abonoBloqueado
                            && ' Sem marcar, a sobra continua no vale para a próxima compra.'}
                        </span>
                      </label>
                      {abonoBloqueado && (
                        <p className="mt-1 flex items-start gap-2 text-xs text-mute">
                          <span className="ponto mt-1 text-alerta" aria-hidden="true" />
                          <span>
                            Sobra acima do que o vendedor pode abonar
                            {tetoAbono !== null ? ` (até ${brl(tetoAbono)})` : ''} — chame o
                            gerente, ou deixe o saldo no vale.
                          </span>
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
              <TotalDaVenda rotulo={usoAVer > 0 ? 'A pagar' : 'Total'} valor={aPagar} />
              {usoAVer > 0 && aPagar === 0 && (
                <p className="flex items-center gap-2 text-xs text-mute">
                  <span className="ponto text-ok" aria-hidden="true" />
                  O vale cobre a venda inteira — nada a receber.
                </p>
              )}
            </div>

            {/* 042: fiado deixou de entrar no caixa — vira dívida. O vendedor
                 precisa saber disso ANTES de fechar, e precisa do nome (o banco
                 recusa fiado sem cliente: não há a quem cobrar). */}
            {forma === 'fiado' && (
              <Aviso tipo="alerta">
                Fiado <strong>não entra no caixa hoje</strong>: vira dívida do cliente,
                e o dinheiro é lançado no dia do acerto. Informe o cliente —
                sem nome, o sistema recusa.
              </Aviso>
            )}
            {dividindo && aDividir <= 0 && (
              <Aviso tipo="alerta">
                O vale cobre a venda inteira — não há o que dividir. Escolha
                qualquer outra forma para fechar.
              </Aviso>
            )}
            {semPreco && <Aviso tipo="alerta">Há item com preço zerado. Informe o preço antes de fechar.</Aviso>}
            {creditoSemParcelas && (
              <Aviso tipo="alerta">
                Falta dizer a bandeira e em quantas vezes.{' '}
                <button type="button" className="underline"
                        onClick={() => setEscolhendoParcelas(true)}>
                  Escolher agora
                </button>
                .
              </Aviso>
            )}
            {modo === 'venda' && temCoringa && coringaSemObservacao && (
              <Aviso tipo="alerta">
                Há PEÇA DIVERSA sem descrição: escreva em cada linha que peça é
                (ex.: "Retrovisor Uno 98 LD"), no campo "Que peça é esta?".
                Sem isso o banco recusa — é o que a auditoria de amanhã vai ler.
              </Aviso>
            )}
            {modo === 'venda' && acimaDoEstoque.length > 0 && (
              <Aviso tipo="alerta">
                {acimaDoEstoque.length} item(ns) acima do estoque registrado. A venda passa e a
                diferença fica pendente de registro — a entrada da mercadoria acerta.
              </Aviso>
            )}
            {modo === 'venda' && venda.error
              ? <Aviso tipo="erro">{mensagemDeErro(venda.error)}</Aviso> : null}

            {modo === 'venda' && (
            <button
              type="button"
              className="botao-primario w-full py-4 text-lg"
              onClick={() => venda.mutate()}
              disabled={venda.isPending || itens.length === 0 || semPreco || coringaSemObservacao
                || divisaoNaoFecha || creditoSemParcelas}
            >
              {venda.isPending ? 'Registrando…' : `Confirmar venda · ${brl(aPagar)}`}
            </button>
            )}

            {/* Cliente só quer o preço: o mesmo carrinho vira orçamento. Na
                aba Orçar não há o que "abrir": a emissão É a tela. */}
            <div className={modo === 'venda' ? 'border-t border-edge pt-4' : ''}>
              {!orcando && modo === 'venda' ? (
                /* Terciário (Grafite): só texto. A ação da tela é fechar a venda;
                   o orçamento é a saída para quando o cliente só quer o preço. */
                <button
                  type="button"
                  className={BOTAO_TERCIARIO}
                  onClick={() => setOrcando(true)}
                  disabled={semPreco}
                >
                  Salvar como orçamento p/ o cliente
                </button>
              ) : (
                <div className="space-y-3">
                  <p className="text-xs text-mute">
                    O orçamento não baixa estoque nem mexe no caixa — congela os preços até a
                    validade e sai pronto para imprimir ou mandar no WhatsApp.
                    {aVer && ' O vale "a ver" não entra no orçamento; aplica-se na hora da venda.'}
                  </p>
                  {/* 049 — a FICHA DA COTAÇÃO. No telefone o vendedor tem
                      poucos segundos: canal em três toques, veículo num campo
                      só ("Gol 2012 1.0 8v", como o cliente fala) e observação
                      livre. Preenchido ou não, o orçamento sai — mas com isto
                      a loja passa a saber o que se cota em cada meio. */}
                  <div>
                    <p className="rotulo">Como o cliente cotou?</p>
                    <div className="grid grid-cols-3 gap-2">
                      {CANAIS_ORCAMENTO.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => setCanalOrc(c.id)}
                          aria-pressed={canalOrc === c.id}
                          className={botaoDeEscolha(canalOrc === c.id)}
                        >
                          {c.rotulo}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className="rotulo" htmlFor="veiculo-orc">Carro (ano, motor)</label>
                      {/* Cliente cadastrado COM carros: escolhe da frota dele
                          (051) — é o que liga a cotação ao carro e alimenta a
                          recorrência. Sem cadastro, ou carro que não está na
                          lista, continua o texto livre: no telefone, o que
                          vale é registrar o que foi dito. */}
                      {(carrosDoCliente.data ?? []).length > 0 && (
                        <select
                          className="campo mb-2"
                          value={carroOrc}
                          onChange={(e) => {
                            setCarroOrc(e.target.value);
                            const v = (carrosDoCliente.data ?? [])
                              .find((x) => x.id === e.target.value);
                            // O texto acompanha a escolha: é ele que congela
                            // no papel do cliente.
                            if (v) {
                              setVeiculoOrc([
                                v.apelido,
                                [v.montadora, v.modelo].filter(Boolean).join(' '),
                                v.ano ? String(v.ano) : null,
                                v.motorizacao,
                                v.placa,
                              ].filter(Boolean).join(' · '));
                            }
                          }}
                        >
                          <option value="">Outro carro (escrever abaixo)</option>
                          {(carrosDoCliente.data ?? []).map((v) => (
                            <option key={v.id} value={v.id}>
                              {v.apelido || [v.montadora, v.modelo].filter(Boolean).join(' ') || v.placa}
                              {v.placa ? ` (${v.placa})` : ''}
                            </option>
                          ))}
                        </select>
                      )}
                      <input
                        id="veiculo-orc"
                        className="campo"
                        placeholder="Ex.: Gol 2012 1.0 8v"
                        value={veiculoOrc}
                        onChange={(e) => { setVeiculoOrc(e.target.value); setCarroOrc(''); }}
                      />
                    </div>
                    <div>
                      <label className="rotulo" htmlFor="obs-orc">Observação (opcional)</label>
                      <input
                        id="obs-orc"
                        className="campo"
                        placeholder="Ex.: vai passar amanhã de manhã"
                        value={obsOrc}
                        onChange={(e) => setObsOrc(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className="rotulo" htmlFor="contato-orc">WhatsApp do cliente (opcional)</label>
                      <input
                        id="contato-orc"
                        className="campo"
                        inputMode="tel"
                        placeholder="(53) 9…"
                        value={contatoCliente}
                        onChange={(e) => setContatoCliente(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="rotulo" htmlFor="validade-orc">Válido até</label>
                      <input
                        id="validade-orc"
                        type="date"
                        className="campo"
                        value={validade}
                        min={hojeISO()}
                        onChange={(e) => setValidade(e.target.value)}
                      />
                    </div>
                  </div>
                  {orcamento.error ? <Aviso tipo="erro">{mensagemDeErro(orcamento.error)}</Aviso> : null}
                  {/* UMA ação principal por tela: na aba Orçar, emitir é ela; na
                      Venda, o primário continua sendo o "Confirmar venda" logo
                      acima, e emitir o orçamento é o secundário. */}
                  <div className="flex gap-2">
                    {modo === 'venda' && (
                      <button type="button" className={`${BOTAO_TERCIARIO} flex-1`}
                              onClick={() => setOrcando(false)}>
                        Voltar
                      </button>
                    )}
                    <button
                      type="button"
                      className={`${modo === 'orcamento' ? 'botao-primario' : 'botao-secundario'} flex-1 py-3`}
                      onClick={() => orcamento.mutate()}
                      disabled={orcamento.isPending || itens.length === 0 || semPreco}
                    >
                      {orcamento.isPending ? 'Emitindo…' : `Emitir orçamento · ${brl(totalCobrado)}`}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </section>
        )}
      </div>

      {/* A coluna direita (só lg): sticky abaixo do header do Layout. */}
      <div className="lg:sticky lg:top-16">
        <CarrinhoLateral
          itens={resumoCarrinho}
          total={total}
          aoRemover={remover}
          aoFechar={irAoFechamento}
          rotuloFechar={modo === 'orcamento' ? 'Emitir orçamento' : undefined}
        />
      </div>
      </div>

      <BarraCarrinho itens={resumoCarrinho} total={total} aoFechar={irAoFechamento}
                     rotuloFechar={modo === 'orcamento' ? 'Orçar' : undefined} />

      {/* A folha "Onde fica" do carrinho: uma peça, ou a volta toda. */}
      {ondeFica?.tipo === 'lista' && (
        <OndeFica aoFechar={() => setOndeFica(null)} lista={comLugar.map(naFolha)} semLugar={fraseSemLugar || null} />
      )}
      {ondeFica?.tipo === 'peca' && (() => {
        const item = itens.find((i) => chaveDe(i) === ondeFica.chave);
        return item ? <OndeFica aoFechar={() => setOndeFica(null)} peca={naFolha(item)} /> : null;
      })()}

      {/* 087: a telinha do crédito. Fora de qualquer coluna, porque é tela
          cheia — no balcão ela precisa tomar o aparelho inteiro.
          FECHAR SEM ESCOLHER volta a forma para dinheiro: deixar 'crédito'
          selecionado sem bandeira prenderia o vendedor num botão desabilitado
          sem dizer por quê. */}
      {escolhendoParcelas && (
        <EscolherParcelas
          total={totalCobrado}
          taxas={taxas.data ?? []}
          aoEscolher={(escolha) => { setCartao(escolha); setEscolhendoParcelas(false); }}
          aoFechar={() => {
            setEscolhendoParcelas(false);
            if (!cartao) setForma('dinheiro');
          }}
        />
      )}
    </Layout>
  );
}
